import React, { useState } from 'react';
import Button from '../../../audi-ui-components/Button';
import IconRecalls from '../../../audi-ui-components/icons/Recalls';
import NavBar from '../../../audi-ui-components/NavBar';
import moment from 'moment';
import { gtmPush } from '../../../lib/gtm';
import _find from 'lodash/find';
import CaretDown from '../../../audi-ui-components/icons/CaretDown';
import CaretUp from '../../../audi-ui-components/icons/CaretUp';
import AlertRed from '../../../audi-ui-components/icons/AlertRed';
import AlertOrange from '../../../audi-ui-components/icons/AlertOrange';
import TickGreen from '../../../audi-ui-components/icons/TickGreen';

const FormatDate = (dateStr) => {
    return moment(dateStr).format('Do MMMM YYYY');
}

const Recalls = ({ recalls, preferredServiceLocation }) => {

    const [activeTab, setActiveTab] = useState(0);


    // **************

    const USE_TEST_DATA = false; // set to false to disable extra test recalls

    // temporary create some test data
    let testRecalls = recalls || [];

    // Find completed recalls and duplicate them
    const completedRecalls = testRecalls.filter(recall =>
        recall.status?.toLowerCase() === 'completed' ||
        recall.status?.toLowerCase() === 'closed' ||
        recall.status?.toLowerCase() === 'repaired'
    );

    if (USE_TEST_DATA && completedRecalls.length > 0) {
        const firstCompleted = completedRecalls[0];

        // Create 2 additional completed recalls with modified names
        const additionalCompleted = [
            {
                ...firstCompleted,
                name: `${firstCompleted.name} - Additional Check 1 TEST`,
                campaignCode: `${firstCompleted.campaignCode}-A1`
            },
            {
                ...firstCompleted,
                name: `${firstCompleted.name} - Additional Check 2 TEST`,
                campaignCode: `${firstCompleted.campaignCode}-A2`
            }
        ];

        // Create 2 outstanding recalls with different names
        const outstandingRecalls = [
            {
                ...firstCompleted,
                name: `Brake System Update - ${firstCompleted.name}`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-OUT1 TEST`,
                campaignType: 'Service Campaign',
                description: 'Brake system software update required for optimal performance',
                repairDate: null
            },
            {
                ...firstCompleted,
                name: `Engine Management Update - ${firstCompleted.name}`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-OUT2 TEST`,
                campaignType: 'Service Campaign',
                description: 'Engine management system requires firmware update',
                repairDate: null
            },
            {
                ...firstCompleted,
                name: `Airbag Safety System Check`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-SAFETY1 TEST`,
                campaignType: 'Safety Recall',
                description: 'Critical safety recall - airbag system requires immediate inspection and potential replacement',
                repairDate: null
            }
        ];

        // Add all test recalls to the array
        testRecalls = [...testRecalls, ...additionalCompleted, ...outstandingRecalls];
    }

    // Use testRecalls instead of recalls for the rest of the component
    recalls = testRecalls;

    // ***********

    const today = moment();

    // split recalls into arrays based on status
    const getStatusGroup = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'closed' || s === 'repaired') return 'Completed';
        if (s === 'outstanding' || s === 'initiated') return 'Outstanding';
        return null;
    };

    const recallsByStatus = recalls?.reduce((groups, recall) => {
        const group = getStatusGroup(recall.status);
        if (!group) return groups;
        if (!groups[group]) groups[group] = [];
        groups[group].push(recall);
        return groups;
    }, {}) || {};
    const hasOutstanding = recallsByStatus['Outstanding']?.length > 0;
    const hasCompleted = recallsByStatus['Completed']?.length > 0;

    const tabActions = [];
    if (hasOutstanding) tabActions.push({ label: "Outstanding" });
    if (hasCompleted) tabActions.push({ label: "Completed" });

    const outstandingTabIndex = hasOutstanding ? 0 : -1;
    const completedTabIndex = hasOutstanding && hasCompleted ? 1 : (hasCompleted ? 0 : -1);

    return (
        <div className="col-12 pb-3 vehicle-details recalls">
            <div className="aui-color-gray95 mt-3 mb-3 pb-5" style={{ height: '100%' }}>
                <div className="row h-100">

                    <div className="col-12 panel">
                        <div className="py-4">
                            <div className="col-12 px-6">
                                <IconRecalls />
                            </div>
                            <div className="col-12 px-6">
                                <h2 className="aui-headline-3"><b>Important Vehicle Updates</b></h2>
                            </div>
                            {(!Array.isArray(recalls) || recalls.length === 0) && (
                                <div className="col-12 px-6 description">
                                    No safety and performance notices found for your vehicle.
                                </div>
                            )}
                            {!(!Array.isArray(recalls) || recalls.length === 0) && (
                                <>
                                    <div className="col-12 px-6 description">
                                        Important safety and performance updates to keep your vehicle at its best.
                                    </div>

                                    <div className="col-12 px-6">
                                        <NavBar
                                            actions={tabActions}
                                            activeIndex={activeTab}
                                            onActionClick={(idx) => setActiveTab(idx)}
                                            navType="tab"
                                            className="recalls-nav mb-4"
                                        />
                                    </div>

                                    <div className="col-12 px-6">
                                        {activeTab === outstandingTabIndex && hasOutstanding && (
                                            <div className="status-group mb-5">
                                                {recallsByStatus['Outstanding'].map((recall, idx) => (
                                                    <div key={`Outstanding-recall-${idx}`} className="pt-6 pb-4 recalls-accordion">
                                                        <details onToggle={(e) => { if (e.currentTarget.open) gtmPush("myAudi_click", "vehicleupdates_accordian", "vehicleupdates_history"); }}>
                                                            <summary>
                                                                <div className="item">
                                                                    <div className="label col-small-3 col-medium-2">
                                                                        {(recall.campaignType === 'Service Campaign' || recall.campaignType === 'Local Campaign') ? <AlertOrange small /> : <AlertRed small />}
                                                                    </div>
                                                                    <div className="value col-small-9 col-medium-10">{recall.campaignType} {recall.campaignCode} - {recall.name}</div>
                                                                </div>
                                                                <div className="carets">
                                                                    <CaretDown className="down" small />
                                                                    <CaretUp className="up" small />
                                                                </div>
                                                            </summary>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Campaign code</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.campaignCode}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Status</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.status}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Description</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.description}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Repair Date</span>
                                                                <span className="value col-small-9 col-medium-10"><a href={`https://forms.audi.com.au/book-a-service${preferredServiceLocation?.dealer?.dealerCode ? `/${preferredServiceLocation.dealer.dealerCode}` : ''}`} target="_blank">Book service</a></span>
                                                            </div>
                                                        </details>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeTab === completedTabIndex && hasCompleted && (
                                            <div className="status-group mb-5">
                                                {recallsByStatus['Completed'].map((recall, idx) => (
                                                    <div key={`Completed-recall-${idx}`} className="pt-6 pb-4 recalls-accordion">
                                                        <details onToggle={(e) => { if (e.currentTarget.open) gtmPush("myAudi_click", "vehicleupdates_accordian", "vehicleupdates_history"); }}>
                                                            <summary>
                                                                <div className="item">
                                                                    <div className="label col-small-3 col-medium-2"><TickGreen small /></div>
                                                                    <div className="value col-small-9">{recall.campaignType} {recall.campaignCode} - {recall.name}</div>
                                                                </div>
                                                                <div className="carets">
                                                                    <CaretDown className="down" small />
                                                                    <CaretUp className="up" small />
                                                                </div>
                                                            </summary>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Campaign code</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.campaignCode}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Status</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.status}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Description</span>
                                                                <span className="value col-small-9 col-medium-10">{recall.description}</span>
                                                            </div>
                                                            <div className="item">
                                                                <span className="label col-small-3 col-medium-2">Repair Date</span>
                                                                <span className="value col-small-9 col-medium-10">{FormatDate(recall.repairDate)}</span>
                                                            </div>
                                                        </details>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                    </div>
                                </>
                            )}

                        </div>
                        <div className="">
                            <div className="buttons px-6">
                                <Button variant="primary" className="" href={`https://forms.audi.com.au/book-a-service${preferredServiceLocation?.dealer?.dealerCode ? `/${preferredServiceLocation.dealer.dealerCode}` : ''}`} target="_blank" onClick={() => gtmPush("myAudi_click", "bookaservice", "Important Vehicle updates cta click")}>Book a service</Button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Recalls;
